import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import api from '../../../services/api';

/**
 * Formulaire de creation/edition d'un Coach (Admin)
 */
const CoachForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    plan: 'free',
    maxClients: 5,
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      fetchCoach();
    }
  }, [id]);

  const fetchCoach = async () => {
    try {
      setFetchLoading(true);
      const response = await api.get(`/admin/coaches/${id}`);
      const coach = response.coach;

      setFormData({
        email: coach.email || '',
        password: '',
        firstName: coach.firstName || '',
        lastName: coach.lastName || '',
        phone: coach.phone || '',
        plan: coach.subscriptionPlan || 'starter',
        maxClients: coach.maxClients || 10,
      });
    } catch (err) {
      console.error('Erreur lors du chargement du coach:', err);
      setError('Impossible de charger les informations du coach.');
    } finally {
      setFetchLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = 'Email requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email invalide';
    }

    if (!isEditMode && !formData.password) {
      errors.password = 'Mot de passe requis';
    } else if (formData.password && formData.password.length < 8) {
      errors.password = 'Minimum 8 caracteres';
    }

    if (!formData.firstName) {
      errors.firstName = 'Prenom requis';
    }

    if (!formData.lastName) {
      errors.lastName = 'Nom requis';
    }

    if (formData.maxClients < 1) {
      errors.maxClients = 'Minimum 1 client';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      if (isEditMode) {
        // Update existing coach
        const updateData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          maxClients: parseInt(formData.maxClients),
        };

        await api.put(`/admin/coaches/${id}`, updateData);

        // Update plan separately if changed
        if (formData.plan) {
          await api.put(`/admin/coaches/${id}/plan`, { plan: formData.plan });
        }

        setSuccess('Coach mis a jour avec succes !');

        // Redirect after short delay
        setTimeout(() => {
          navigate('/admin/coaches');
        }, 1500);
      } else {
        // Create new coach
        await api.post('/admin/coaches', {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          plan: formData.plan,
        });

        setSuccess('Coach cree avec succes !');

        // Redirect after short delay
        setTimeout(() => {
          navigate('/admin/coaches');
        }, 1500);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.message || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEditMode ? 'Modifier le coach' : 'Nouveau coach'} - Admin Yoco</title>
      </Helmet>

      <Box>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate('/admin/coaches')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h1">
            {isEditMode ? 'Modifier le coach' : 'Nouveau coach'}
          </Typography>
        </Stack>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Form */}
        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {/* Section: Informations personnelles */}
                <Typography variant="h3" sx={{ mb: 1 }}>
                  Informations personnelles
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Prenom"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    error={!!formErrors.firstName}
                    helperText={formErrors.firstName}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Nom"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    error={!!formErrors.lastName}
                    helperText={formErrors.lastName}
                    required
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    required
                    disabled={isEditMode}
                  />
                  <TextField
                    fullWidth
                    label="Telephone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+33 6 12 34 56 78"
                  />
                </Stack>

                {/* Password field - only for creation or if user wants to change */}
                {!isEditMode && (
                  <TextField
                    fullWidth
                    label="Mot de passe"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    error={!!formErrors.password}
                    helperText={formErrors.password || 'Minimum 8 caracteres'}
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                {/* Password section - only in edit mode */}
                {isEditMode && (
                  <>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h3" sx={{ mb: 1 }}>
                      Changer le mot de passe
                    </Typography>

                    {passwordSuccess && (
                      <Alert severity="success" onClose={() => setPasswordSuccess(null)}>
                        {passwordSuccess}
                      </Alert>
                    )}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                      <TextField
                        fullWidth
                        label="Nouveau mot de passe"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setNewPasswordError(null);
                        }}
                        error={!!newPasswordError}
                        helperText={newPasswordError || 'Minimum 8 caractères'}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                edge="end"
                              >
                                {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Button
                        variant="outlined"
                        disabled={passwordLoading || !newPassword}
                        onClick={async () => {
                          if (newPassword.length < 8) {
                            setNewPasswordError('Minimum 8 caractères');
                            return;
                          }
                          try {
                            setPasswordLoading(true);
                            setNewPasswordError(null);
                            await api.put(`/admin/coaches/${id}/password`, { password: newPassword });
                            setPasswordSuccess('Mot de passe mis à jour avec succès');
                            setNewPassword('');
                          } catch (err) {
                            setNewPasswordError(err.response?.data?.message || 'Erreur lors de la mise à jour');
                          } finally {
                            setPasswordLoading(false);
                          }
                        }}
                        sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content', height: 56 }}
                      >
                        {passwordLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                        Mettre à jour
                      </Button>
                    </Stack>
                  </>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Section: Abonnement */}
                <Typography variant="h3" sx={{ mb: 1 }}>
                  Abonnement
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Plan</InputLabel>
                    <Select
                      name="plan"
                      value={formData.plan}
                      onChange={handleChange}
                      label="Plan"
                    >
                      <MenuItem value="free">Gratuit (5 clients)</MenuItem>
                      <MenuItem value="starter">Starter (10 clients)</MenuItem>
                      <MenuItem value="pro">Pro (50 clients)</MenuItem>
                      <MenuItem value="enterprise">Enterprise (illimite)</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Nombre max de clients"
                    name="maxClients"
                    type="number"
                    value={formData.maxClients}
                    onChange={handleChange}
                    error={!!formErrors.maxClients}
                    helperText={formErrors.maxClients}
                    inputProps={{ min: 1 }}
                  />
                </Stack>

                <Divider sx={{ my: 2 }} />

                {/* Actions */}
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/admin/coaches')}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    {isEditMode ? 'Enregistrer' : 'Creer le coach'}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default CoachForm;
